/** The Christmas base's message, as the order sheet checks it: the same rules
 *  /api/checkout refuses a message by (functions/src/lib/christmas.ts), copied
 *  as they are, so the sheet says "too long" and keeps Continue disabled for
 *  exactly the messages the server would refuse, and never lets one through
 *  that it would. check-templates loads this very file and runs it beside the
 *  server's on the same messages, sizes and products.
 *
 *  Plain TypeScript the way Node can load it by stripping the types (no
 *  imports, no enums, no parameter properties), like multiBuy.ts. The limits
 *  are not here: they are the server's (the seed's christmas.base.maxChars),
 *  sent with the page's read as `christmas.base.maxChars`, and passed in.
 *  Nor is the season: the page is told whether it is Christmas, never works
 *  it out. */

export type Extra = "christmas_hat" | "christmas_sweater";
export type MaxChars = Record<string, Record<string, number> | undefined>;

/** The products that stand on a base: only they take the Christmas base. */
export const BASE_KINDS: readonly string[] = ["minime", "duo", "pet_figure"];

/** The kind the base is asked by for a piece: a statue of the animals alone
 *  (its offer's `figure`) has its own sizes and limits, "pet_figure", whatever
 *  the lead's kind (minime) is. */
export function baseKindOf<K extends string>(kind: K | undefined, offer?: { figure?: boolean } | null): K | "pet_figure" | undefined {
  return offer?.figure ? "pet_figure" : kind;
}

export function takesBase(kind: string | null | undefined): boolean {
  return BASE_KINDS.includes(String(kind ?? ""));
}

/** The most characters any message may be, whatever the size. */
export const MAX_MESSAGE_CHARS = 40;

/** Latin letters and their accents, digits, a space and a little punctuation:
 *  all the base's font can print. No Chinese, no emoji, and only the Latin
 *  Extended-B letters the font has (functions/src/lib/christmas.ts says
 *  which; check-templates keeps the two the same). */
export const BASE_CHARSET = /^[0-9A-Za-zÀ-žƏƒƠơƯưǄ-ǜǦǧǪǫǺ-țȪ-ȭȰ-ȳȷ .,!?'’&+\-:()]*$/u;

/** A message as it will be raised on the base: composed, one line, single
 *  spaces, trimmed. Empty is a base with no words, which is fine. */
export function cleanMessage(raw: unknown): string {
  return String(raw ?? "")
    .slice(0, 400)
    .normalize("NFC")
    .replace(/\s+/gu, " ")
    .trim();
}

/** Its length as the counter shows it ("12 of 20"): characters, not bytes. */
export function messageLength(raw: unknown): number {
  return [...cleanMessage(raw)].length;
}

/** How many characters fit on this product at this size, or 0 for no base. */
export function messageLimit(kind: string, size: string, maxChars: MaxChars): number {
  const limit = Number(maxChars[kind]?.[size]);
  return Number.isFinite(limit) && limit > 0 ? Math.min(Math.floor(limit), MAX_MESSAGE_CHARS) : 0;
}

/** Why a message cannot go on this base, or null when it can: "no-base" (this
 *  product or size takes none), "charset" (a character the font lacks),
 *  "too-long" (never cut: the sheet says so and suggests a bigger size). */
export type MessageProblem = "no-base" | "charset" | "too-long";

export function messageProblem(raw: unknown, kind: string, size: string, maxChars: MaxChars): MessageProblem | null {
  const limit = messageLimit(kind, size, maxChars);
  if (!limit) return "no-base";
  const text = cleanMessage(raw);
  if (!BASE_CHARSET.test(text)) return "charset";
  if ([...text].length > limit) return "too-long";
  return null;
}

/** The extras in the fewest characters, as a paid line's metadata keeps them:
 *  "h", "s", "hs" or "". */
export function extrasCode(extras: readonly Extra[]): string {
  return [extras.includes("christmas_hat") ? "h" : "", extras.includes("christmas_sweater") ? "s" : ""].join("");
}
